/**
 * Band reorder for ABSOLUTE email artboards.
 *
 * Figma only reorders children of auto-layout frames — free-form frames just
 * free-move. But an email is semantically a vertical stack even when its
 * bands are absolutely positioned (agent-authored scenes, layoutMode NONE),
 * so the editor treats the artboard's top-level FRAME/GROUP children as a
 * reorderable stack: dragging a band past a neighbor's midpoint restacks the
 * y positions, with the same ghost-preview feel as auto-layout dragReorder.
 *
 * Gap preservation: the stack's gap sequence (top offset + the gap between
 * each slot, which can be negative for overlap/transition strips) is kept by
 * SLOT, and bands slot into it — so a reorder never accumulates drift and the
 * authored rhythm of the stack survives.
 *
 * Pure helpers shared by the store (commit on endDrag) and the render-doc
 * builder (drag preview).
 */
import type { SceneNode, SceneDocument } from '@/lib/scene-document';

import { cloneNodes } from './canvaskit-mutations';

/** True when `node` is the single-root, absolutely-positioned email artboard. */
export function isAbsoluteArtboard(doc: SceneDocument, node: SceneNode): boolean {
  return (
    doc.rootChildren.length === 1 &&
    doc.rootChildren[0] === node.id &&
    node.type === 'FRAME' &&
    (node.layoutMode ?? 'NONE') === 'NONE'
  );
}

/**
 * The artboard's band stack: direct FRAME/GROUP children in visual (y) order.
 * Loose leaves (text, shapes, decorations placed straight on the artboard)
 * are not bands and keep their positions during a restack.
 */
export function bandStack(doc: SceneDocument, parentId: string): string[] {
  const parent = doc.nodes[parentId];
  return (parent?.children ?? [])
    .map((id) => doc.nodes[id])
    .filter(
      (n): n is SceneNode =>
        n != null &&
        n.visible !== false &&
        (n.type === 'FRAME' || n.type === 'GROUP') &&
        n.slotRole !== 'link-zone',
    )
    .sort((a, b) => a.y - b.y)
    .map((n) => n.id);
}

/**
 * New y for every band when `draggedId` moves to `toIndex` in the stack.
 * Returns patches keyed by band id (only bands whose y changes).
 */
export function restackBands(
  doc: SceneDocument,
  parentId: string,
  draggedId: string,
  toIndex: number,
): Map<string, { y: number }> {
  const stack = bandStack(doc, parentId);
  const fromIndex = stack.indexOf(draggedId);
  const patches = new Map<string, { y: number }>();
  if (fromIndex < 0) return patches;

  // Per-slot gaps from the current stack: gap[0] = top offset, gap[i] = space
  // between slot i-1's bottom and slot i's top (negative = overlap).
  const gaps: number[] = stack.map((id, i) => {
    const node = doc.nodes[id];
    if (i === 0) return node.y;
    const prev = doc.nodes[stack[i - 1]];
    return node.y - (prev.y + prev.height);
  });

  const order = [...stack];
  order.splice(fromIndex, 1);
  order.splice(Math.min(toIndex, order.length), 0, draggedId);

  let cursor = 0;
  for (let i = 0; i < order.length; i += 1) {
    const node = doc.nodes[order[i]];
    const y = (i === 0 ? 0 : cursor) + gaps[i];
    if (Math.abs(y - node.y) > 0.01) patches.set(order[i], { y });
    cursor = y + node.height;
  }
  return patches;
}

/**
 * Children array reordered to match the new visual stack order (keeps the
 * layers panel and paint order in sync with the restack). Non-band children
 * keep their relative positions.
 */
export function reorderChildrenToStack(
  parent: SceneNode,
  doc: SceneDocument,
  draggedId: string,
  toIndex: number,
): string[] {
  const stack = bandStack(doc, parent.id);
  const fromIndex = stack.indexOf(draggedId);
  if (fromIndex < 0) return parent.children ?? [];

  const order = [...stack];
  order.splice(fromIndex, 1);
  order.splice(Math.min(toIndex, order.length), 0, draggedId);

  // Walk the original children; replace band ids in encounter order with the
  // new stack order, leaving non-band children untouched.
  const bandSet = new Set(stack);
  let next = 0;
  return (parent.children ?? []).map((id) => (bandSet.has(id) ? order[next++] : id));
}

/**
 * Duplicate a top-level section band as the NEXT band of the email stack:
 * the copy lands directly below the original and every band beneath shifts
 * down by the band's height — what auto-layout reflow would do, extended to
 * absolute artboards. Returns null when `bandId` is not a band of a stacked
 * artboard (callers fall back to plain sibling duplication).
 */
export function duplicateBandBelow(
  doc: SceneDocument,
  bandId: string,
): { doc: SceneDocument; newId: string } | null {
  const band = doc.nodes[bandId];
  if (!band || (band.type !== 'FRAME' && band.type !== 'GROUP')) return null;
  if (band.slotRole === 'link-zone') return null;
  if (!band.parentId) return null;

  const artboard = doc.nodes[band.parentId];
  if (!artboard) return null;
  const isVerticalArtboard =
    doc.rootChildren.length === 1 &&
    doc.rootChildren[0] === artboard.id &&
    artboard.type === 'FRAME' &&
    artboard.layoutMode === 'VERTICAL';
  if (!isAbsoluteArtboard(doc, artboard) && !isVerticalArtboard) return null;

  const { nodes, newIds } = cloneNodes(doc, [bandId], { dx: 0, dy: 0 });
  const newId = newIds[0];
  const nextNodes = { ...doc.nodes, ...nodes };

  if ((artboard.layoutMode ?? 'NONE') === 'NONE') {
    // Absolute artboard: open a slot under the original and shift the rest.
    const insertY = band.y + band.height;
    for (const cid of artboard.children ?? []) {
      const child = nextNodes[cid];
      if (child && child.y >= insertY - 0.5) {
        nextNodes[cid] = { ...child, y: child.y + band.height };
      }
    }
    nextNodes[newId] = { ...nextNodes[newId], y: insertY };
  }

  const children = [...(artboard.children ?? [])];
  children.splice(children.indexOf(bandId) + 1, 0, newId);
  nextNodes[artboard.id] = { ...artboard, children };

  return { doc: { ...doc, nodes: nextNodes }, newId };
}
